import React from 'react';
import { Pause, CheckCircle2, Circle, Lock, TrendingUp, TrendingDown, Minus } from 'lucide-react';

export const pnlCls = (v) => (v > 0 ? 'text-emerald-300' : v < 0 ? 'text-rose-300' : 'text-slate-300');
export const money = (v) => `${v >= 0 ? '+' : ''}${Number(v || 0).toFixed(2)}$`;
const LEVEL_COLORS = ['bg-slate-500', 'bg-sky-500', 'bg-cyan-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500'];

export function Card({ title, right, children, testId, className = '' }) {
    return (
        <div className={`rounded-xl border border-slate-700/70 bg-slate-900/60 p-3 space-y-2 ${className}`} data-testid={testId}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">{title}</span>
                {right}
            </div>
            {children}
        </div>
    );
}

/** Bot öğrenme seviyesi: toplam kapanışla yükselir; sonraki seviyeye ilerleme çubuğu. */
export function LevelCard({ level }) {
    if (!level) return null;
    return (
        <Card title="Bot öğrenme seviyesi" testId="learn-level-card">
            <div className="flex items-end gap-3">
                <div className={`h-12 w-12 rounded-xl ${LEVEL_COLORS[level.level] || 'bg-slate-500'} grid place-items-center text-slate-950 font-black text-xl`} data-testid="learn-level-number">{level.level}</div>
                <div className="min-w-0">
                    <div className="text-base font-bold text-slate-50" data-testid="learn-level-name">{level.name} <span className="text-[11px] font-normal text-slate-400">· seviye {level.level}/{level.max}</span></div>
                    <div className="text-[11px] text-slate-400">{level.desc}</div>
                </div>
            </div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className={`h-full ${LEVEL_COLORS[level.level] || 'bg-slate-500'} transition-[width] duration-700`} style={{ width: `${level.progress_pct}%` }} /></div>
            <div className="text-[11px] text-slate-400" data-testid="learn-level-progress">
                {level.total} değerlendirilen kapanış{level.next_at ? ` · ${level.next_name} için ${level.remaining} kapanış daha (${level.next_at})` : ' · en üst seviye'}
            </div>
        </Card>
    );
}

/** Trader notu: kâr faktörüne göre; isabet, kâr/zarar oranı, başabaş için gereken isabet, 7 günlük eğilim. */
export function TraderCard({ trader }) {
    if (!trader) return null;
    const t = trader.trend;
    const TrendIcon = t?.dir === 'up' ? TrendingUp : t?.dir === 'down' ? TrendingDown : Minus;
    return (
        <Card title="Trader notu" testId="learn-trader-card" right={t && (
            <span className={`inline-flex items-center gap-1 text-[10px] ${t.dir === 'up' ? 'text-emerald-300' : t.dir === 'down' ? 'text-rose-300' : 'text-slate-400'}`} data-testid="learn-trader-trend">
                <TrendIcon className="h-3 w-3" /> son 7 gün kâr faktörü {t.last7} (önceki {t.prev7}) — {t.dir === 'up' ? 'gelişiyor' : t.dir === 'down' ? 'geriliyor' : 'yatay'}
            </span>
        )}>
            <div className="text-base font-bold text-slate-50" data-testid="learn-trader-name">{trader.name}{trader.grade != null && <span className="text-[11px] font-normal text-slate-400"> · not {trader.grade}/{trader.max}</span>}</div>
            <div className="text-[11px] text-slate-400">{trader.desc}</div>
            {trader.grade != null && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                    <Stat label="Kâr faktörü" value={trader.profit_factor} hint="brüt kâr ÷ brüt zarar · 1 = başabaş" testId="learn-trader-pf" cls={trader.profit_factor >= 1 ? 'text-emerald-300' : 'text-rose-300'} />
                    <Stat label="İsabet" value={`%${trader.win_rate}`} hint={trader.breakeven_win_rate != null ? `bu kâr/zarar oranıyla başabaş için %${trader.breakeven_win_rate} gerekir` : ''} testId="learn-trader-wr" cls={trader.breakeven_win_rate != null && trader.win_rate >= trader.breakeven_win_rate ? 'text-emerald-300' : 'text-amber-300'} />
                    <Stat label="Ort. kâr / ort. zarar" value={`${Number(trader.avg_win).toFixed(1)}$ / ${Number(trader.avg_loss).toFixed(1)}$`} hint={trader.rr != null ? `oran ${trader.rr} — kazanç başına ${trader.rr < 1 ? 'zarar daha büyük' : 'zarardan büyük'}` : ''} testId="learn-trader-rr" cls={trader.rr >= 1 ? 'text-emerald-300' : 'text-rose-300'} />
                    <Stat label="İşlem başına beklenti" value={money(trader.expectancy)} hint={`${trader.n} kapanış`} testId="learn-trader-exp" cls={pnlCls(trader.expectancy)} />
                </div>
            )}
        </Card>
    );
}

function Stat({ label, value, hint, testId, cls = 'text-slate-100' }) {
    return (
        <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2 py-1.5" data-testid={testId}>
            <div className="text-[10px] text-slate-500">{label}</div>
            <div className={`font-mono font-bold ${cls}`}>{value}</div>
            {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
        </div>
    );
}

const STATUS_ICON = { done: [CheckCircle2, 'text-emerald-300'], active: [Circle, 'text-sky-300'], locked: [Lock, 'text-slate-500'] };

/** Gelişim planı: hangi adım tamamlandı, hangisi sürüyor, hangisi kilitli. */
export function Roadmap({ steps }) {
    if (!steps?.length) return null;
    return (
        <Card title="Gelişim planı" testId="learn-roadmap">
            <ol className="space-y-1.5">
                {steps.map((s) => {
                    const [Icon, cls] = STATUS_ICON[s.status] || STATUS_ICON.locked;
                    return (
                        <li key={s.key} className="flex items-start gap-2 text-xs" data-testid={`learn-roadmap-${s.key}`}>
                            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cls}`} />
                            <div className="min-w-0">
                                <span className={s.status === 'done' ? 'text-slate-300 line-through decoration-slate-600' : s.status === 'active' ? 'text-slate-100' : 'text-slate-500'}>{s.title}</span>
                                <span className="ml-2 font-mono text-[10px] text-slate-400">{s.progress}</span>
                                {s.detail && <div className="text-[10px] text-slate-500">{s.detail}</div>}
                                {s.note && <div className="text-[10px] text-amber-300">{s.note}</div>}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </Card>
    );
}

export function PausedBadge({ paused }) {
    if (!paused?.length) return null;
    return (
        <span className="inline-flex items-center gap-1 text-[10px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid="learn-paused">
            <Pause className="h-3 w-3" /> durduruldu: {paused.map((p) => `${p.label} (${Math.round(p.hours_left)} sa)`).join(' · ')}
        </span>
    );
}
